import { useMemo } from "react";
import NextLink from "next/link";

import { cn } from "@releaseit/ui";

import ChevronLeftIcon from "@releaseit/assets/icons/chevron-left.svg";
import ChevronRightIcon from "@releaseit/assets/icons/chevron-right.svg";
import CrossIcon from "@releaseit/assets/icons/cross.svg";

import { NavigationLink, NavigationQuery } from "@/generated/graphql";
import { useHash } from "@/hooks/use-hash";

import { getCategoriesMap } from "../../helpers/get-categories-map";
import { NavCategory } from "../../types";

type MobileCategoriesNavProps = {
  categories: NavigationQuery["Navigation"]["categories"];
};

export function MobileCategoriesNav({ categories }: MobileCategoriesNavProps) {
  const selectedHref = useHash();

  const categoriesMap = useMemo(() => getCategoriesMap(categories), [categories]);
  const selected = categoriesMap.get(selectedHref as string);

  return (
    <>
      {/* Hide the default layout */}
      <style>{`
        @media screen and (max-width: 768px) {
          #header,
          #header-promo-bar,
          #footer {
            display: none;
          }
        }
      `}</style>

      <div className="md:hidden">
        {/* Toolbar */}
        <div className="sticky top-0 flex items-center justify-between border-b bg-white p-4">
          <NextLink href={selected?.parentHref ? `#${selected.parentHref}` : "/category"} shallow>
            <a className={cn(!selected && "invisible")}>
              <span className="sr-only">Back</span>
              <ChevronLeftIcon className="h-5 w-5" />
            </a>
          </NextLink>

          <h2 className="font-medium tracking-wide">{selected?.title ?? "Categories"}</h2>

          <NextLink href="/">
            <a>
              <span className="sr-only">Home</span>
              <CrossIcon className="h-5 w-5" />
            </a>
          </NextLink>
        </div>

        <div className="space-y-8 pt-2 pb-8">
          {/* View all */}
          {selected && (
            <NextLink href={`/hire/${selected.href}`}>
              <a className="mx-4 flex border-b py-2.5 text-sm text-gray-800">View all</a>
            </NextLink>
          )}

          {/* Featured categories */}
          {selected?.featured && selected.featured.length > 0 && (
            <NavSection
              title="Featured categories"
              itemType="category"
              items={selected?.featured}
            />
          )}

          {/* All categories */}
          <NavSection
            title={selected && "All categories"}
            itemType="category"
            items={selected?.children ?? categories}
          />

          {/* Popular brands */}
          {selected?.__typename === "NavigationTopCategory" && selected.featured.length > 0 && (
            <NavSection title="Popular brands" itemType="brand" items={selected?.popularBrands} />
          )}
        </div>
      </div>
    </>
  );
}

/** Internal components */

type NavSectionProps = {
  title?: string;
  itemType: "category" | "brand";
  items: (NavCategory | NavigationLink)[];
};

function NavSection({ title, itemType, items }: NavSectionProps) {
  return (
    <section className="px-4">
      {title && <h3 className="text-lg font-medium">{title}</h3>}

      <nav>
        {items.map((category) => (
          <NavItem key={category.href} itemType={itemType} item={category} />
        ))}
      </nav>
    </section>
  );
}

type NavItemProps = {
  itemType: "category" | "brand";
  item: NavCategory | NavigationLink;
};

function NavItem({ itemType, item }: NavItemProps) {
  const hrefBase = itemType === "category" ? "/hire" : "/brand";

  const hasChildren = "children" in item;
  const isLeafNode = !hasChildren || (item.children && item.children.length === 0);

  return (
    <NextLink href={isLeafNode ? `${hrefBase}/${item.href}` : `#${item.href}`} shallow>
      <a className="flex items-center justify-between truncate border-b py-3 text-sm text-gray-800">
        <span>{item.title}</span>
        {!isLeafNode && <ChevronRightIcon className="h-4 w-4" />}
      </a>
    </NextLink>
  );
}
